// Bundled Station C template graphics — exported from Figma DP1eoxbusBnlm3ekBuMUmm (2026-09-22) at
// the 2160×3840 design size (tmp/render-template-assets.mjs renders the Figma markup without the
// editable nodes). Applying a template only places "asset:" placeholders; the editor draws them from
// the bundled URL. Saving the slide uploads the needed files into the current organisation's media
// library once (matched by originalName) so the player, offline cache and thumbnails keep using
// ordinary media ids (2026-10-14 대교: 적용만으로 라이브러리에 복제되지 않게, 저장할 때 올린다).
import softBlueBg from '../assets/stationc-templates/soft-blue-bg.jpg';
import cardFrameNews from '../assets/stationc-templates/card-frame-news.png';
import cardFrameNotice from '../assets/stationc-templates/card-frame-notice.png';
import poster1Bg from '../assets/stationc-templates/poster1-bg.mp4';
import poster1Still from '../assets/stationc-templates/poster1-bg.jpg';
import poster2Bg from '../assets/stationc-templates/poster2-bg.mp4';
import poster2Still from '../assets/stationc-templates/poster2-bg.jpg';
import poster3Overlay from '../assets/stationc-templates/poster3-overlay.png';
import greetingBg from '../assets/stationc-templates/greeting-bg.mp4';
import greetingStill from '../assets/stationc-templates/greeting-bg.jpg';
import campaignBg from '../assets/stationc-templates/campaign-bg.mp4';
import campaignStill from '../assets/stationc-templates/campaign-bg.jpg';
import christmasBg from '../assets/stationc-templates/christmas-bg.mp4';
import christmasStill from '../assets/stationc-templates/christmas-bg.jpg';
import logoStationC from '../assets/stationc-templates/logo-stationc.png';
import logoHallym from '../assets/stationc-templates/logo-hallym.png';
import logoHallymWhite from '../assets/stationc-templates/logo-hallym-white.png';
import logoHallymDark from '../assets/stationc-templates/logo-hallym-dark.png';
import samplePhoto from '../assets/stationc-templates/sample-photo.jpg';
import type { MediaItem } from '@cornerstone/shared';

export type StationCTemplateAsset = { key: string; url: string; fileName: string; mime: 'image/jpeg' | 'image/png' | 'video/mp4'; label: string;
  /** Still frame the gallery preview shows for a video asset (opening the gallery must not start a decoder per card). */
  poster?: string };

const MIME = { jpg: 'image/jpeg', png: 'image/png', mp4: 'video/mp4' } as const;
const asset = (key: string, url: string, ext: keyof typeof MIME, label: string, poster?: string): StationCTemplateAsset =>
  ({ key, url, fileName: `stationc-template-${key}.${ext}`, mime: MIME[ext], label, ...(poster ? { poster } : {}) });
export const isVideoAsset = (item: StationCTemplateAsset): boolean => item.mime === 'video/mp4';

export const STATIONC_TEMPLATE_ASSETS = {
  'soft-blue-bg': asset('soft-blue-bg', softBlueBg, 'jpg', '연한 파란 배경'),
  'card-frame-news': asset('card-frame-news', cardFrameNews, 'png', '카드뉴스 카드 프레임'),
  'card-frame-notice': asset('card-frame-notice', cardFrameNotice, 'png', '공지사항 카드 프레임'),
  'poster1-bg': asset('poster1-bg', poster1Bg, 'mp4', '행사 포스터 ① 배경 영상', poster1Still),
  'poster2-bg': asset('poster2-bg', poster2Bg, 'mp4', '행사 포스터 ② 배경 영상', poster2Still),
  'poster3-overlay': asset('poster3-overlay', poster3Overlay, 'png', '행사 포스터 ③ 어둡게 오버레이'),
  'greeting-bg': asset('greeting-bg', greetingBg, 'mp4', '그리팅 배경 영상', greetingStill),
  'campaign-bg': asset('campaign-bg', campaignBg, 'mp4', '캠페인(신년·설·추석) 배경 영상', campaignStill),
  'christmas-bg': asset('christmas-bg', christmasBg, 'mp4', '크리스마스 배경 영상', christmasStill),
  'logo-stationc': asset('logo-stationc', logoStationC, 'png', 'Station C 로고'),
  'logo-hallym': asset('logo-hallym', logoHallym, 'png', '한림대학교 로고'),
  'logo-hallym-white': asset('logo-hallym-white', logoHallymWhite, 'png', '한림대학교 로고 (흰색)'),
  'logo-hallym-dark': asset('logo-hallym-dark', logoHallymDark, 'png', '한림대학교 로고 (어두운 배경용)'),
  'sample-photo': asset('sample-photo', samplePhoto, 'jpg', '샘플 사진'),
} as const satisfies Record<string, StationCTemplateAsset>;

export type StationCTemplateAssetKey = keyof typeof STATIONC_TEMPLATE_ASSETS;

/** Placeholder media id used inside a template draft until `resolveStationCTemplateAssets` uploads the file. */
export const ASSET_MEDIA_PREFIX = 'asset:';
export const assetMediaId = (key: StationCTemplateAssetKey): string => `${ASSET_MEDIA_PREFIX}${key}`;
export function assetForMediaId(mediaId: string): StationCTemplateAsset | null {
  if (!mediaId.startsWith(ASSET_MEDIA_PREFIX)) return null;
  const key = mediaId.slice(ASSET_MEDIA_PREFIX.length) as StationCTemplateAssetKey;
  return STATIONC_TEMPLATE_ASSETS[key] ?? null;
}

/** 저장 전 템플릿 그래픽의 파일·미리보기 주소(api.mediaFileUrl / mediaThumbnailUrl 이 "asset:" id 에 쓴다). */
export function templateAssetUrl(mediaId: string, kind: 'file' | 'thumbnail'): string | null {
  const item = assetForMediaId(mediaId);
  if (!item) return null;
  return kind === 'thumbnail' ? item.poster ?? item.url : item.url;
}

/**
 * 편집기가 저장 전 템플릿 그래픽을 보통 미디어처럼 그리도록 하는 가상 항목. 라이브러리 목록(고르는 창)에는 넣지 않고
 * 편집기의 id → 미디어 표에만 넣는다. 저장할 때 resolveStationCTemplateAssets 가 진짜 미디어로 바꿔 끼운다.
 */
export const TEMPLATE_ASSET_MEDIA: readonly MediaItem[] = (Object.keys(STATIONC_TEMPLATE_ASSETS) as StationCTemplateAssetKey[]).map((key) => {
  const item: StationCTemplateAsset = STATIONC_TEMPLATE_ASSETS[key];
  return {
    id: assetMediaId(key), filename: item.fileName, originalName: item.fileName, mimeType: item.mime,
    mediaType: isVideoAsset(item) ? 'video' : 'image', sizeBytes: 0, durationSec: null, width: null, height: null,
    relPath: item.fileName, thumbnailRelPath: isVideoAsset(item) && !item.poster ? null : item.fileName,
    processingStatus: 'ready', variants: [], createdAt: '1970-01-01T00:00:00.000Z', lastPlayedAt: null,
  } satisfies MediaItem;
});
